(() => {
  const STORAGE_KEY = 'field_signal_attribution_v1';
  const CART_SIGNATURE_KEY = 'field_signal_cart_attribution_signature_v1';
  const SESSION_KEY = 'field_signal_session_id_v1';
  const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const DEFAULT_RELEASE = 'dr-relaunch-2026-09-21';
  const ATTRIBUTION_KEYS = [
    'fs_release',
    'fs_creative',
    'fs_angle',
    'fs_offer',
    'fs_landing',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'utm_source_platform',
    'fbclid',
    'ad_id',
    'adset_id',
    'campaign_id',
    'trybe',
  ];
  const SELECTION_KEYS = [
    'fs_protection_test',
    'fs_model',
    'fs_quantity',
    'fs_offer_selection',
    'fs_variant_id',
    'fs_protection_variant_id',
    'fs_protection_quantity',
    'fs_protection_selected',
    'fs_order_total_cents',
    'fs_currency',
    'fs_add_event_id',
    'fs_checkout_event_id',
  ];
  const PRIVATE_PROPERTY_KEYS = new Set([...ATTRIBUTION_KEYS, ...SELECTION_KEYS, 'fs_session_id']);
  let attribution = {};
  let cartSyncPromise = null;

  function safeStorage(storage, operation, ...args) {
    try {
      return storage?.[operation]?.(...args);
    } catch (_) {
      return null;
    }
  }

  function sanitize(value, maxLength = 200) {
    return String(value ?? '')
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .trim()
      .slice(0, maxLength);
  }

  function randomId(prefix) {
    const value = window.crypto?.randomUUID?.() ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    return `${prefix}-${value}`;
  }

  function sessionId() {
    const existing = safeStorage(window.sessionStorage, 'getItem', SESSION_KEY);
    if (existing) return existing;

    const created = randomId('fs');
    safeStorage(window.sessionStorage, 'setItem', SESSION_KEY, created);
    return created;
  }

  function readStoredAttribution() {
    const raw = safeStorage(window.localStorage, 'getItem', STORAGE_KEY);
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      const capturedAt = Number(parsed.captured_at || 0);
      if (!capturedAt || Date.now() - capturedAt > ATTRIBUTION_TTL_MS) {
        safeStorage(window.localStorage, 'removeItem', STORAGE_KEY);
        return {};
      }

      return ATTRIBUTION_KEYS.reduce((result, key) => {
        const value = sanitize(parsed[key]);
        if (value) result[key] = value;
        return result;
      }, { captured_at: capturedAt });
    } catch (_) {
      safeStorage(window.localStorage, 'removeItem', STORAGE_KEY);
      return {};
    }
  }

  function captureAttribution() {
    const stored = readStoredAttribution();
    const params = new URLSearchParams(window.location.search);
    const startsNewTouch = [
      'fs_release',
      'fs_landing',
      'utm_source',
      'utm_campaign',
      'utm_id',
      'fbclid',
      'campaign_id',
    ].some((key) => sanitize(params.get(key)));
    const captured = startsNewTouch ? {} : { ...stored };
    let hasIncomingAttribution = false;

    ATTRIBUTION_KEYS.forEach((key) => {
      const value = sanitize(params.get(key));
      if (!value) return;
      captured[key] = value;
      hasIncomingAttribution = true;
    });

    const trybeId = sanitize(params.get('trybe'));
    if (trybeId) {
      if (!captured.fs_creative) captured.fs_creative = `trybe-${trybeId}`;
      if (!captured.fs_angle) captured.fs_angle = 'trybe-ugc';
      if (!captured.fs_offer) captured.fs_offer = 'pro-quantity-protection';
    }

    const paidLanding = document.querySelector('[data-fs-ad-landing]');
    if (paidLanding) {
      if (!captured.fs_release) captured.fs_release = DEFAULT_RELEASE;
      if (!captured.fs_landing || hasIncomingAttribution) {
        captured.fs_landing = sanitize(window.location.pathname, 300);
      }
    }

    if (hasIncomingAttribution || (!stored.captured_at && Object.keys(captured).length)) {
      captured.captured_at = Date.now();
    }

    if (captured.captured_at) {
      safeStorage(window.localStorage, 'setItem', STORAGE_KEY, JSON.stringify(captured));
    }

    attribution = ATTRIBUTION_KEYS.reduce((result, key) => {
      const value = sanitize(captured[key], key === 'fs_landing' ? 300 : 200);
      if (value) result[key] = value;
      return result;
    }, {});
  }

  function normalizedFields(extra = {}) {
    const fields = { ...attribution, fs_session_id: sessionId() };
    [...ATTRIBUTION_KEYS, ...SELECTION_KEYS].forEach((key) => {
      if (!(key in extra)) return;
      const value = sanitize(extra[key]);
      if (value || value === '0') fields[key] = value;
    });
    return fields;
  }

  function getLineItemProperties(extra = {}) {
    return Object.entries(normalizedFields(extra)).reduce((properties, [key, value]) => {
      if (PRIVATE_PROPERTY_KEYS.has(key) && value) properties[`_${key}`] = value;
      return properties;
    }, {});
  }

  function addHiddenProperties(form, extra = {}) {
    if (!(form instanceof HTMLFormElement)) return;

    const properties = getLineItemProperties(extra);
    Object.entries(properties).forEach(([key, value]) => {
      const name = `properties[${key}]`;
      let input = Array.from(form.elements).find((element) => element.name === name);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.fsAttributionProperty = 'true';
        form.appendChild(input);
      }
      input.value = value;
    });
  }

  function rootUrl(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}${path.replace(/^\//, '')}`;
  }

  function publish(name, detail = {}) {
    const payload = {
      ...detail,
      attribution: { ...attribution },
      fs_session_id: sessionId(),
      path: window.location.pathname,
      occurred_at: new Date().toISOString(),
    };

    document.dispatchEvent(
      new CustomEvent(`fieldsignal:tracked:${name}`, { detail: payload })
    );

    try {
      Promise.resolve(window.Shopify?.analytics?.publish?.(`field_signal:${name}`, payload)).catch(
        () => {}
      );
    } catch (_) {
      // Tracking must never block the purchase path.
    }

    return payload;
  }

  function createEventId(eventName) {
    return randomId(`fs-${sanitize(eventName, 40).toLowerCase() || 'event'}`);
  }

  function trackCommerce(eventName, detail = {}, eventId = createEventId(eventName)) {
    return publish('commerce_event', {
      ...detail,
      event_name: eventName,
      event_id: eventId,
    });
  }

  function syncCartAttributes(extra = {}, options = {}) {
    const attributes = normalizedFields(extra);
    delete attributes.fs_session_id;
    if (!Object.keys(attributes).length) return Promise.resolve(true);

    const signature = JSON.stringify(attributes);
    const previousSignature = safeStorage(window.sessionStorage, 'getItem', CART_SIGNATURE_KEY);
    if (!options.force && previousSignature === signature) return Promise.resolve(true);
    if (cartSyncPromise) {
      if (!options.force) return cartSyncPromise;
      return cartSyncPromise.then(() => syncCartAttributes(extra, { force: true }));
    }

    cartSyncPromise = fetch(rootUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart attribution sync returned ${response.status}`);
        safeStorage(window.sessionStorage, 'setItem', CART_SIGNATURE_KEY, signature);
        publish('attribution_sync', { success: true, keys: Object.keys(attributes) });
        return true;
      })
      .catch((error) => {
        publish('attribution_sync', {
          success: false,
          error: sanitize(error?.message, 160),
          keys: Object.keys(attributes),
        });
        return false;
      })
      .finally(() => {
        cartSyncPromise = null;
      });

    return cartSyncPromise;
  }

  function currentSelection() {
    const landing = document.querySelector('[data-fs-ad-landing]');
    const selectedTab = landing?.querySelector('[role="tab"][aria-selected="true"]');
    const selectedVersion = selectedTab?.dataset.fsAdVersion || '';
    const panel = selectedVersion
      ? landing?.querySelector(`.fs-pdp-purchase-panel[data-fs-ad-version="${selectedVersion}"]`)
      : landing?.querySelector('.fs-pdp-purchase-panel:not([hidden])[data-fs-ad-version]') ||
        landing?.querySelector('.fs-pdp-purchase-panel[data-fs-ad-version]');
    const version = selectedVersion || panel?.dataset.fsAdVersion || '';
    const offer = panel?.querySelector('[data-fs-ad-offer-selected]');
    const plan = panel?.querySelector('[data-fs-ad-protection-choice="plan"]');
    const units = sanitize(offer?.dataset.fsAdUnits || '');
    const planSelected = Boolean(plan?.checked && !plan.disabled);

    return {
      fs_model: sanitize(version),
      fs_quantity: units,
      fs_offer_selection: sanitize(offer?.dataset.fsAdOfferLabel || ''),
      fs_variant_id: sanitize(offer?.dataset.fsAdVariantId || offer?.dataset.variantId || ''),
      fs_protection_variant_id: planSelected
        ? sanitize(plan?.dataset.fsAdProtectionVariant || '')
        : '',
      fs_protection_quantity: planSelected ? units : '0',
      fs_protection_selected: planSelected ? 'true' : 'false',
    };
  }

  function publishLandingEvents() {
    const landing = document.querySelector('[data-fs-ad-landing]');
    if (!landing) return;

    const selection = currentSelection();
    trackCommerce('ViewContent', selection);

    const plan = landing.querySelector('[data-fs-ad-protection-choice="plan"]');
    if (plan) {
      publish('protection_plan_impression', {
        ...selection,
        protection_variant_id: plan.dataset.fsAdProtectionVariant || '',
      });
    }
  }

  captureAttribution();

  window.FieldSignalAttribution = Object.freeze({
    version: '1.0.0',
    get: () => ({ ...attribution }),
    currentSelection,
    getLineItemProperties,
    addHiddenProperties,
    syncCartAttributes,
    createEventId,
    trackDiagnostic: publish,
    trackCommerce,
  });

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof Element ? event.target.closest('form') : null;
      if (form?.matches('form[action*="/cart/add"]')) addHiddenProperties(form, currentSelection());
    },
    true
  );

  document.addEventListener('fieldsignal:bundle_select', (event) => {
    publish('offer_select', { ...currentSelection(), ...event.detail });
  });
  document.addEventListener('fieldsignal:version_change', (event) => {
    publish('model_select', { ...currentSelection(), ...event.detail });
  });
  document.addEventListener('fieldsignal:protection_plan_select', (event) => {
    const selected = event.detail?.selected === true;
    publish(selected ? 'protection_plan_selected' : 'protection_plan_removed', {
      ...currentSelection(),
      ...event.detail,
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
          addHiddenProperties(form, currentSelection());
        });
        window.setTimeout(publishLandingEvents, 0);
        syncCartAttributes({}, { force: Boolean(document.querySelector('[data-fs-ad-landing]')) });
      },
      { once: true }
    );
  } else {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      addHiddenProperties(form, currentSelection());
    });
    window.setTimeout(publishLandingEvents, 0);
    syncCartAttributes({}, { force: Boolean(document.querySelector('[data-fs-ad-landing]')) });
  }
})();
